<template>
    <button
        :id="id"
        :class="['fab', {active, internal}]"
        :disabled="disabled"
        @click="handleClick"
    >
        <!-- Custom icon implementation to be displayed in the FAB-->
        <slot>
            <!-- `<FontAwesomeIcon />` -->
            <FontAwesomeIcon :icon="getIcon" />
        </slot>

        <b-tooltip
            placement="top"
            boundary="body"
            :show="tooltipShow"
            :target="id"
            :title="getTitle"
            :triggers="tooltipTrigger"
        />
    </button>
</template>

<script>
    import {library} from '@fortawesome/fontawesome-svg-core';
    import {
        faArrows,
        faArrowToBottom,
        faCommentPlus,
        faCompressAlt,
        faCopy,
        faExpandAlt,
        faPen,
        faTrash,
    } from '@fortawesome/pro-solid-svg-icons';
    import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome';
    import uniqueId from 'lodash/uniqueId';

    library.add(
        faArrows,
        faArrowToBottom,
        faCommentPlus,
        faCompressAlt,
        faCopy,
        faExpandAlt,
        faPen,
        faTrash
    );

    /**
     * @group Buttons
     * A component to utilize a Kit Car Floating Action Button.
     * <br /><br />
     * __Note:__ Any `FontAwesome` icon may be used, but it must follow
     * the same styling, or be defined by UX.
     */
    export default {
        name: 'FloatingActionButton',
        components: {
            FontAwesomeIcon,
        },
        model: {
            prop: 'active',
            event: 'toggled',
        },
        props: {
            /**
             * Sets the active state of the FAB; supplies the value for `v-model`
             */
            active: {
                required: false,
                type: Boolean,
            },
            /**
             * When supplied or set to `true`, disables the component's functionality and
             * places it in a disabled state
             */
            disabled: {
                required: false,
                type: Boolean,
            },
            /**
             * Denotes a custom icon to be displayed. Should only be set when type is 'custom'
             */
            icon: {
                // `undefined`
                default: undefined,
                required: false,
                type: [Array, String, undefined],
            },
            /**
             * Sets the 'id' attribute on the button
             */
            id: {
                type: String,
                default: () => {
                    return uniqueId();
                }
            },
            /**
             * When supplied or set to `true`, denotes that the FAB is meant for internal-only
             * usage and applies the appropriate styling for internal-only
             */
            internal: {
                required: false,
                type: Boolean,
            },
            /**
             * When supplied or set to `true`, manually shows the FAB's tooltip
             */
            tooltipShow: {
                required: false,
                type: Boolean,
            },
            /**
             * Sets the trigger type of the tooltip
             */
            tooltipTrigger: {
                // `hover focus`
                default: 'hover focus',
                required: false,
                type: String,
            },
            /**
             * Gives the FAB's tooltip custom wording
             */
            tooltipWording: {
                // `undefined`
                default: undefined,
                required: false,
                type: String,
            },
            /**
             * Type of FAB
             */
            type: {
                // `custom`
                default: 'custom',
                required: false,
                // `edit` / `collapse` / `delete` / `comment` / `move` / `download` / `copy` / `custom`
                type: String,
                validator: (value) => {
                    return ['edit', 'collapse', 'delete', 'comment', 'move', 'download', 'copy', 'custom'].includes(value);
                },
            },
        },
        computed: {
            getIcon() {
                return {
                    edit: 'pen',
                    collapse: this.active ? 'expand-alt' : 'compress-alt',
                    delete: 'trash',
                    comment: 'comment-plus',
                    move: 'arrows',
                    download: 'arrow-to-bottom',
                    copy: 'copy',
                    custom: this.icon,
                }[this.type];
            },
            getTitle() {
                return this.tooltipWording || {
                    edit: 'Edit',
                    collapse: this.active ? 'Expand' : 'Collapse',
                    delete: 'Delete',
                    comment: 'Add a Comment',
                    move: 'Move',
                    download: 'Download',
                    copy: 'Copy',
                }[this.type];
            },
        },
        methods: {
            handleClick(event) {
                /**
                 * Emitted when the button is clicked; is used when binding via `v-model` for
                 * updating the `active` prop
                 */
                this.$emit('click', event);

                /**
                 * Same as the `click` event
                 */
                this.$emit('toggled', event);
            }
        }
    };
</script>
